import {
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import * as actions from "../../api/actions/pos";
import { connect } from "react-redux";
import {
  SalesAccWidget,
  SalesChart,
  PeriodicalSalesReports,
  PurchaseAndSellChart,
  StoreValueReport,
  TopTenItems,
  SalesByEmployee,
  ProfitBreakdown,
  TopSellingProdTable,
  StoreExpenses,
} from "../index";
import { useContext } from "react";
import { AppContext } from "../../App";
import { useState } from "react";
import { useEffect } from "react";
import { api, controller, endpoints } from "../../api/actions/api";
import { GetMonths } from "../../hooks/Method";
import { formatCurrency } from "../../hooks/Currency";

const Home = (props) => {
  const { t } = useTranslation();
  const { _store, _getById, _authUser } = props;
  const { setLoader } = useContext(AppContext);
  const [sellsPerMonth, setSellsPerMonth] = useState([]);
  const [purchPerMonth, setPurchPerMonth] = useState([]);
  const [sellsItem, setSellsItem] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [sells, setSells] = useState([]);
  const [yearlySells, setYearlySells] = useState(0);
  const [montlySells, setMontlySells] = useState(0);
  const [weeklySells, setWeeklySells] = useState(0);
  const [dailySells, setDailySells] = useState(0);
  const [currentInvVal, setCurrentInvVal] = useState(0);
  const [products, setProducts] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [customerBalances, setCustomerBalances] = useState([]);

  //Periodical Sales && Sort Product Sells
    useEffect(() => {
      const _sells = sells.flatMap((j) =>
        (j.sellItems || []).map((y) => ({
            ...y,
            sellDate: j.sellDate,
            userId: j.userId,
            user: j.user,
        }))
      );

      setSellsItem(_sells);

      const now = new Date();

    const _yearlySells = _sells
      .filter((j) => {
        const sellDate = new Date(`${j.sellDate}Z`);

        return sellDate.getFullYear() === now.getFullYear();
      })
      .map((j) => j.price * j.qty)
      .reduce((x, y) => x + y, 0);
    setYearlySells(_yearlySells);
  
    const _monthlySells = _sells
      .filter((j) => {
        const sellDate = new Date(`${j.sellDate}Z`);

        return (
          sellDate.getFullYear() === now.getFullYear() &&
          sellDate.getMonth() === now.getMonth()
        );
      })
      .map((j) => j.price * j.qty)
      .reduce((x, y) => x + y, 0);

    setMontlySells(_monthlySells);

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    const _weeklySells = _sells
      .filter((j) => {
        const sellDate = new Date(`${j.sellDate}Z`);

        return sellDate >= startOfWeek && sellDate < endOfWeek;
      })
      .map((j) => j.price * j.qty)
      .reduce((x, y) => x + y, 0);

    setWeeklySells(_weeklySells);

    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(startOfDay);
    endOfDay.setDate(startOfDay.getDate() + 1);

    const _dailySells = _sells
      .filter((j) => {
        const sellDate = new Date(`${j.sellDate}Z`);

        return sellDate >= startOfDay && sellDate < endOfDay;
      })
      .map((j) => j.price * j.qty)
      .reduce((x, y) => x + y, 0);

    setDailySells(_dailySells);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sells]);

    const onComplete = (res) => {
        if (!res) {
          setLoader(false);
          return;
        }

        if (res.status === 200) {
          setSells(res.result || []);
        }

        setLoader(false);
      };

    const invOnComplete = (res, err) => {
      // ✅ Handle Axios / server errors first
      if (err || !res) {
        console.error("Inventory API error:", err);

        // optional UI feedback
        setInventories([]);
        setCurrentInvVal(0);
        setLoader(false);

        return;
      }

  // ✅ Handle success
  if (res.status === 200 && Array.isArray(res.result)) {
    setInventories(res.result);

    const total = res.result.reduce(
      (sum, j) => sum + (j.product?.purchPrice ?? 0) * (j.inInventory ?? 0),
      0
    );

    setCurrentInvVal(total);
    setLoader(false);
  }
};

  // Get customers with outstanding balances for selected store
  useEffect(() => {
    if (!_store?.id) {
      setCustomerBalances([]);
      return;
    }

    api(
      `${endpoints.Customer}${controller.CustomerBalanceReport}/${_store.id}`
    )
      .get()
      .then((res) => {
        if (
          res?.data?.status === 200 &&
          Array.isArray(res.data.result)
        ) {
          const balances = res.data.result.filter(
            (customer) => Number(customer.balance) > 0
          );

          setCustomerBalances(balances);
        } else {
          setCustomerBalances([]);
        }
      })
      .catch((err) => {
        console.error("Customer Balance Report:", err);
        setCustomerBalances([]);
      });

  }, [_store]);

  // Get Datas from the server
  useEffect(() => {
    if (_store.id !== undefined && _authUser?.role === 1) {
      setLoader(true);

      // Get Sell By Store Id List
      _getById(
        endpoints.Sell + controller.GetSellsByStore,
        _store.id,
        null,
        onComplete
      );

      // Get Purchases
      _getById(
        endpoints.Purchase + controller.GetByStore,
        _store.id,
        null,
        (res) => res.status === 200 && 
          setPurchases(Array.isArray(res.result) ? res.result : [])
      );

      // Get In Inventory Values
      _getById(
        endpoints.Inventory + controller.GetProdsByStore,
        _store.id,
        null,
        invOnComplete
      );

      // Get Expenses
      _getById(
        endpoints.Expense + controller.GetByStoreId,
        _store.id,
        null,
        (res) => res.status === 200 && 
        setExpenses(Array.isArray(res.result) ? res.result : [])
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store.id]);

  // Get sells per month
  useEffect(() => {
    const months = GetMonths();

    const _sellsPerMonth = months.map((month) =>
      sellsItem.filter((x) => {
        const sellDate = new Date(`${x.sellDate}Z`);

        return (
          sellDate.getMonth() + 1 === Number(month.split("/")[0]) &&
          sellDate.getFullYear() === Number(month.split("/")[1])
        );
      })
    );

  // ...rest of your existing code

  setSellsPerMonth(
      _sellsPerMonth.map((items) =>
        Number(
          items
            .reduce((sum, item) => sum + item.price * item.qty, 0)
            .toFixed(2)
        )
      )
    );

    // Sort Product By Sells
    const filteredProds = [];
    const _prods = sellsItem.filter((j) => j.itemType === 1);

    _prods.forEach((j) => {
      const prod = _prods.filter(
        (x) => x.product.id === j.product.id
      );

      !filteredProds.some(
        (x) => JSON.stringify(x) === JSON.stringify(prod)
      ) && filteredProds.push(prod);
    });

    setProducts(filteredProds);
  }, [sellsItem]);

  // Get purchase per month
    useEffect(() => {
      const months = GetMonths();
      const now = new Date();

      const _purchPerMonth = months.map((month) =>
        purchases.filter((x) => {
          const purchDate = new Date(`${x.purchDate}Z`);

          return (
            purchDate.getMonth() === months.indexOf(month) &&
            purchDate.getFullYear() === now.getFullYear()
          );
        })
      );

    setPurchPerMonth(
      _purchPerMonth.map((j) =>
        j
          .map((purchase) =>
            purchase.purchItems
              .map((item) => item.qty * item.purchPrice)
              .reduce((x, y) => x + y, 0)
          )
          .reduce((x, y) => x + y, 0)
      )
    );
  }, [purchases]);

  return (
    <Grid container spacing={3} className="tx-upp">

    <Grid item xs={12}>
      <PeriodicalSalesReports
        {...{ yearlySells, montlySells, weeklySells, dailySells }}
      />
    </Grid>

    <Grid item xs={12} md={5}>
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            {t("customersWithBalanceDue")}
          </Typography>

          {customerBalances.length === 0 ? (
            <Typography>{t("noOutstandingBalances")}</Typography>
          ) : (
            <>
              {customerBalances.map((customer) => (
                <Grid
                  container
                  key={customer.customerId}
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography>
                    {customer.customerName}
                  </Typography>

                  <Typography>
                    {formatCurrency(customer.balance, _store)}
                  </Typography>
                </Grid>
              ))}

              <Grid
                container
                justifyContent="space-between"
                sx={{
                  mt: 2,
                  pt: 1,
                  borderTop: "1px solid #ddd",
                }}
              >
                <Typography fontWeight="bold">
                  {t("totalDue")}
                </Typography>

                <Typography fontWeight="bold">
                  {formatCurrency(
                    customerBalances.reduce(
                      (total, customer) =>
                        total + Number(customer.balance || 0),
                      0
                    ),
                    _store
                  )}
                </Typography>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Grid>

      <Grid item xs={12} md={5}>
        <SalesAccWidget {...{ sellsItem, expenses, purchases }} />
      </Grid>
      <Grid item xs={12} md={7}>
        <SalesChart {...{ sellsPerMonth }} />
      </Grid>
      <Grid item xs={12} md={9}>
        <PurchaseAndSellChart {...{ sellsPerMonth, purchPerMonth }} />
      </Grid>
      <Grid item xs={12} md={3}>
        <StoreValueReport {...{ currentInvVal }} />
      </Grid>
      <Grid item xs={12}>
        <TopTenItems {...{ products }} />
      </Grid>
      <Grid item xs={12} md={4}>
        <SalesByEmployee {...{ sellsItem }} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ProfitBreakdown {...{ sellsItem, expenses }} />
      </Grid>
      <Grid item xs={12} md={4}>
        <StoreExpenses {...{ expenses }} />
      </Grid>
      <Grid item xs={12}>
        <TopSellingProdTable {...{ sellsItem, inventories }} />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
  _authUser: state.pos.authUser,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(mapStateToProps, mapActionToProps)(Home);
